import React, { useState, useEffect, useRef } from "react";

function WoWSection({ sessionId }) {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://localhost:3443";

  const authHeaders = {
    Authorization: `Bearer ${sessionId || ""}`,
    "Content-Type": "application/json",
  };

  const [rosterData, setRosterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  /** Nom dans le roster au moment d'ouvrir l'édition — utilisé dans l'URL PUT (l'API identifie par l'ancien nom). */
  const [editingMemberOriginalName, setEditingMemberOriginalName] =
    useState(null);
  const [notification, setNotification] = useState(null);
  const [newMember, setNewMember] = useState({
    Name: "",
    Class: "",
    Role: "",
    DiscordID: "",
    URL: "",
  });

  // Protection contre les appels simultanés avec useRef
  const isLoadingRosterRef = useRef(false);

  // Fonction pour afficher une notification
  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Fonction pour charger le roster depuis l'API
  const loadRoster = async () => {
    // Protection contre les appels simultanés avec useRef
    if (isLoadingRosterRef.current) {
      return;
    }

    try {
      isLoadingRosterRef.current = true;
      const response = await fetch(`${API_BASE_URL}/api/roster`, {
        headers: authHeaders,
      });
      if (response.ok) {
        const data = await response.json();
        setRosterData(data);
      } else {
        console.error("Erreur lors du chargement du roster");
        if (response.status === 401) {
          showNotification(
            "❌ Session expirée. Veuillez vous reconnecter.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement du roster:", error);
      // Ne pas afficher d'erreur si c'est juste le serveur qui redémarre
      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("ECONNREFUSED")
      ) {
        setTimeout(() => loadRoster(), 2000);
        return;
      }
      showNotification("❌ Erreur de connexion au serveur", "error");
    }
    setLoading(false);
    isLoadingRosterRef.current = false;
  };

  useEffect(() => {
    loadRoster();
  }, []);

  // Fonction pour ajouter un membre
  const handleAddMember = async (e, retryCount = 0) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_BASE_URL}/api/roster/member`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(newMember),
      });

      if (!response.ok) {
        if (response.status === 401) {
          showNotification(
            "❌ Session expirée. Veuillez vous reconnecter.",
            "error"
          );
          return;
        }
        const result = await response.json();
        showNotification(`❌ Erreur: ${result.message}`, "error");
        return;
      }

      const result = await response.json();

      if (result.success) {
        showNotification("✅ Membre ajouté avec succès !", "success");
        setNewMember({ Name: "", Class: "", Role: "", DiscordID: "", URL: "" });
        setShowAddModal(false);
        loadRoster(); // Recharger le roster
      } else {
        showNotification(`❌ Erreur: ${result.message}`, "error");
      }
    } catch (error) {
      console.error("💥 Erreur lors de l'ajout du membre:", error);
      // Ne pas afficher d'erreur si c'est juste le serveur qui redémarre
      if (
        error.message.includes("Failed to fetch") ||
        error.message.includes("ECONNREFUSED")
      ) {
        if (retryCount < 3) {
          setTimeout(() => handleAddMember(e, retryCount + 1), 2000);
          return;
        }
        showNotification("❌ Erreur de connexion au serveur", "error");
        return;
      }
      showNotification("❌ Erreur de connexion au serveur", "error");
    }
  };

  // Fonction pour modifier un membre
  const handleEditMember = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/roster/member/${encodeURIComponent(
          editingMemberOriginalName
        )}`,
        {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify(editingMember),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          showNotification(
            "❌ Session expirée. Veuillez vous reconnecter.",
            "error"
          );
          return;
        }
        const result = await response.json();
        showNotification(`❌ Erreur: ${result.message}`, "error");
        return;
      }

      const result = await response.json();

      if (result.success) {
        showNotification("✅ Membre modifié avec succès !", "success");
        setEditingMember(null);
        setEditingMemberOriginalName(null);
        setShowEditModal(false);
        loadRoster(); // Recharger le roster
      } else {
        showNotification(`❌ Erreur: ${result.message}`, "error");
      }
    } catch (error) {
      console.error("Erreur lors de la modification du membre:", error);
      showNotification("❌ Erreur de connexion au serveur", "error");
    }
  };

  // Fonction pour supprimer un membre
  const handleToggleMemberDisabled = async (memberName, nextDisabled) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/roster/member/${encodeURIComponent(
          memberName
        )}/disabled`,
        {
          method: "PATCH",
          headers: authHeaders,
          body: JSON.stringify({ disabled: nextDisabled }),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          showNotification(
            "❌ Session expirée. Veuillez vous reconnecter.",
            "error"
          );
          return;
        }
        const result = await response.json().catch(() => ({}));
        showNotification(
          `❌ Erreur: ${result.message || response.statusText}`,
          "error"
        );
        return;
      }

      const result = await response.json();
      if (result.success) {
        showNotification(
          nextDisabled
            ? "🔕 Membre exclu des annonces d'événements"
            : "🔔 Membre réintégré dans les annonces d'événements",
          "success"
        );
        loadRoster();
      } else {
        showNotification(`❌ Erreur: ${result.message}`, "error");
      }
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      showNotification("❌ Erreur de connexion au serveur", "error");
    }
  };

  const handleDeleteMember = async (memberName) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer ${memberName} du roster ?`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/roster/member/${encodeURIComponent(memberName)}`,
        {
          method: "DELETE",
          headers: authHeaders,
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          showNotification(
            "❌ Session expirée. Veuillez vous reconnecter.",
            "error"
          );
          return;
        }
        const result = await response.json();
        showNotification(`❌ Erreur: ${result.message}`, "error");
        return;
      }

      const result = await response.json();

      if (result.success) {
        showNotification("✅ Membre supprimé avec succès !", "success");
        loadRoster(); // Recharger le roster
      } else {
        showNotification(`❌ Erreur: ${result.message}`, "error");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du membre:", error);
      showNotification("❌ Erreur de connexion au serveur", "error");
    }
  };

  // Fonction pour ouvrir le modal d'édition
  const openEditModal = (member) => {
    setEditingMemberOriginalName(member.Name);
    setEditingMember({ ...member });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingMember(null);
    setEditingMemberOriginalName(null);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "Tank":
        return "#ff6b6b";
      case "Heal":
        return "#51cf66";
      case "DPS":
        return "#339af0";
      default:
        return "#868e96";
    }
  };

  const getClassColor = (className) => {
    const classColors = {
      Warrior: "#c41e3a",
      Paladin: "#f58cba",
      Hunter: "#aad372",
      Rogue: "#fff468",
      Priest: "#ffffff",
      Shaman: "#0070dd",
      Mage: "#3fc7eb",
      Warlock: "#a330c9",
      Monk: "#00ff96",
      Druid: "#ff7c0a",
      DemonHunter: "#a330c9",
      Evoker: "#33937f",
      DeathKnight: "#c40000",
    };
    return classColors[className] || "#868e96";
  };

  const matchesFilters = (member) => {
    const matchesRole = filterRole === "all" || member.Role === filterRole;
    const matchesSearch =
      member.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.Class.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesSearch;
  };

  const filteredActiveMembers =
    rosterData?.Members?.filter(
      (m) => !m.Disabled && matchesFilters(m)
    ) || [];

  const filteredDisabledMembers =
    rosterData?.Members?.filter(
      (m) => m.Disabled && matchesFilters(m)
    ) || [];

  const roles = [
    "all",
    ...new Set(rosterData?.Members?.map((m) => m.Role) || []),
  ];

  const classes = [
    "Warrior",
    "Paladin",
    "Hunter",
    "Rogue",
    "Priest",
    "Shaman",
    "Mage",
    "Warlock",
    "Monk",
    "Druid",
    "DemonHunter",
    "Evoker",
    "DeathKnight",
  ];

  const roleOptions = ["Tank", "Heal", "DPS"];

  const renderMemberCard = (member) => (
    <div
      key={member.Name}
      className={`member-card${member.Disabled ? " member-card-disabled" : ""}`}
      style={member.URL ? { cursor: "pointer" } : {}}
      onClick={() => {
        if (member.URL) {
          window.open(member.URL, "_blank", "noopener,noreferrer");
        }
      }}
    >
      <div className="member-header">
        <h3 className="member-name">
          {member.Name}
          {member.Disabled && (
            <span className="member-disabled-badge">Désactivé</span>
          )}
        </h3>
        <div className="member-actions">
          <button
            type="button"
            className="toggle-roster-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleMemberDisabled(member.Name, !member.Disabled);
            }}
            title={
              member.Disabled
                ? "Réactiver"
                : "Désactiver"
            }
          >
            {member.Disabled ? "🔔" : "🔕"}
          </button>
          <button
            className="edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(member);
            }}
            title="Modifier"
          >
            ✏️
          </button>
          <button
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteMember(member.Name);
            }}
            title="Supprimer"
          >
            🗑️
          </button>
        </div>
      </div>
      <div className="member-details">
        <div className="member-class">
          <span
            className="class-icon"
            style={{ backgroundColor: getClassColor(member.Class) }}
          >
            {member.Class.charAt(0)}
          </span>
          <span className="class-name">{member.Class}</span>
        </div>
        <div className="member-role">
          <span
            className="role-badge"
            style={{ backgroundColor: getRoleColor(member.Role) }}
          >
            {member.Role}
          </span>
        </div>
      </div>
      <div className="member-footer">
        <span className="last-update-text">
          Mis à jour :{" "}
          {member.LastUpdate
            ? new Date(
                member.LastUpdate.replace(" ", "T")
              ).toLocaleDateString("fr-FR")
            : "Date inconnue"}
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement du roster...</p>
      </div>
    );
  }

  return (
    <div className="wow-section">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <header className="section-header">
        <h1>🎮 World of Warcraft</h1>
        <p>Gestionnaire de roster WoW</p>
        {rosterData?.LastUpdate && (
          <p className="last-update">
            Dernière mise à jour :{" "}
            {rosterData.LastUpdate.DateTime ||
              (typeof rosterData.LastUpdate === "string"
                ? new Date(rosterData.LastUpdate).toLocaleString("fr-FR")
                : new Date().toLocaleString("fr-FR"))}
          </p>
        )}
      </header>

      <div className="stats-container">
        <div className="stat-card">
          <h3>Total</h3>
          <span className="stat-number">
            {rosterData?.Members?.filter((m) => !m.Disabled).length || 0}
          </span>
          {(rosterData?.Members?.filter((m) => m.Disabled).length || 0) >
            0 && (
            <p className="stat-hint">
              {rosterData.Members.filter((m) => m.Disabled).length} désactivé
              {rosterData.Members.filter((m) => m.Disabled).length > 1
                ? "s"
                : ""}
            </p>
          )}
        </div>
        <div className="stat-card">
          <h3>Tanks</h3>
          <span className="stat-number">
            {rosterData?.Members?.filter(
              (m) => !m.Disabled && m.Role === "Tank"
            ).length || 0}
          </span>
        </div>
        <div className="stat-card">
          <h3>Heals</h3>
          <span className="stat-number">
            {rosterData?.Members?.filter(
              (m) => !m.Disabled && m.Role === "Heal"
            ).length || 0}
          </span>
        </div>
        <div className="stat-card">
          <h3>DPS</h3>
          <span className="stat-number">
            {rosterData?.Members?.filter(
              (m) => !m.Disabled && m.Role === "DPS"
            ).length || 0}
          </span>
        </div>
      </div>

      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher un joueur ou une classe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="role-filters">
          {roles.map((role) => (
            <button
              key={role}
              className={`role-filter ${filterRole === role ? "active" : ""}`}
              onClick={() => setFilterRole(role)}
              style={{
                backgroundColor:
                  role === "all" ? "#868e96" : getRoleColor(role),
              }}
            >
              {role === "all" ? "Tous" : role}
            </button>
          ))}
        </div>
      </div>

      <div className="roster-actions">
        <button
          className="add-member-btn"
          onClick={() => setShowAddModal(true)}
        >
          ➕ Ajouter un membre
        </button>
      </div>

      <div className="roster-container">
        {filteredActiveMembers.length === 0 ? (
          <div className="no-results">
            <p>Aucun membre actif ne correspond à ces critères</p>
          </div>
        ) : (
          <div className="members-grid">
            {filteredActiveMembers.map((m) => renderMemberCard(m))}
          </div>
        )}
      </div>

      {filteredDisabledMembers.length > 0 && (
        <div className="roster-disabled-section">
          <h2 className="roster-disabled-heading">Membres désactivés</h2>
          <p className="roster-disabled-subtitle">
            Exclus des annonces d&apos;événements
          </p>
          <div className="roster-container roster-container-disabled">
            <div className="members-grid">
              {filteredDisabledMembers.map((m) => renderMemberCard(m))}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'ajout de membre */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Ajouter un nouveau membre</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>
            <form
              onSubmit={handleAddMember}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            >
              <div className="form-group">
                <label>Nom du personnage *</label>
                <input
                  type="text"
                  value={newMember.Name}
                  onChange={(e) =>
                    setNewMember({ ...newMember, Name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Classe *</label>
                  <select
                    value={newMember.Class}
                    onChange={(e) =>
                      setNewMember({ ...newMember, Class: e.target.value })
                    }
                    required
                  >
                    <option value="">Sélectionner une classe</option>
                    {classes.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rôle *</label>
                  <select
                    value={newMember.Role}
                    onChange={(e) =>
                      setNewMember({ ...newMember, Role: e.target.value })
                    }
                    required
                  >
                    <option value="">Sélectionner un rôle</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>ID Discord (optionnel)</label>
                <input
                  type="text"
                  value={newMember.DiscordID}
                  onChange={(e) =>
                    setNewMember({ ...newMember, DiscordID: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>URL du personnage (optionnel)</label>
                <input
                  type="url"
                  value={newMember.URL}
                  onChange={(e) =>
                    setNewMember({ ...newMember, URL: e.target.value })
                  }
                  placeholder="https://worldofwarcraft.blizzard.com/..."
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  Ajouter le membre
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'édition de membre */}
      {showEditModal && editingMember && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Modifier {editingMember.Name}</h3>
              <button className="close-btn" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <form
              onSubmit={handleEditMember}
              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
            >
              <div className="form-group">
                <label>Nom du personnage *</label>
                <input
                  type="text"
                  value={editingMember.Name}
                  onChange={(e) =>
                    setEditingMember({ ...editingMember, Name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Classe *</label>
                  <select
                    value={editingMember.Class}
                    onChange={(e) =>
                      setEditingMember({
                        ...editingMember,
                        Class: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Sélectionner une classe</option>
                    {classes.map((cls) => (
                      <option key={cls} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Rôle *</label>
                  <select
                    value={editingMember.Role}
                    onChange={(e) =>
                      setEditingMember({
                        ...editingMember,
                        Role: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Sélectionner un rôle</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>ID Discord (optionnel)</label>
                <input
                  type="text"
                  value={editingMember.DiscordID}
                  onChange={(e) =>
                    setEditingMember({
                      ...editingMember,
                      DiscordID: e.target.value,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>URL du personnage (optionnel)</label>
                <input
                  type="url"
                  value={editingMember.URL}
                  onChange={(e) =>
                    setEditingMember({ ...editingMember, URL: e.target.value })
                  }
                  placeholder="https://worldofwarcraft.blizzard.com/..."
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn">
                  Sauvegarder les modifications
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={closeEditModal}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WoWSection;
